import { gql } from '@apollo/client';
import algoliasearch from 'algoliasearch';
import { useRouter } from 'next/router';
import * as React from 'react';
import Cookies from 'universal-cookie';

import { search_query_submitted } from '@/analytics';
import { useCurrent } from '@/hooks/useCurrent';
import { logError } from '@/imports/logging/ClientLogger';
import { useQueryWithPastDataDeprecated } from '@/imports/ui/useQueryWithPastData';

import { getSearchKeys } from './graphql-types/getSearchKeys';

import { TenantSearchMode } from './utils';

const cookie = new Cookies();

export type AiSearchHttpConfig = {
  baseUrl: string;
  buildHeaders: () => HeadersInit;
};

type ISearchContext = {
  client: ISearchClientProxy | null;
  searchMode: 'algolia' | 'opensearch';
  indexData?: {
    postIndexName: string;
    orgIndexName: string;
    eventIndexName: string;
    fileIndexName: string;
    memberIndexName: string;
    memberIndexSearchableField: string;
    memberIndexViewableField: string;
  };
  indexFilterData?: {
    memberIndexFilter: string;
    orgIndexFilter?: string | null;
  };
  /** AI Search REST (multi-index queries and ai-ask) when search keys are loaded. */
  aiSearchHttp: AiSearchHttpConfig | null;
};

type ISearchClientProxy = {
  search: (
    queries: readonly IInstantSearchQuery[],
    requestOptions?: unknown
  ) => Promise<{ results: Array<Record<string, unknown>> }>;
  clearCache: () => Promise<void>;
};

type IInstantSearchQuery = {
  indexName: string;
  params?: {
    query?: string;
    page?: number;
    hitsPerPage?: number;
    filters?: string;
    facets?: string[] | string;
    attributesToRetrieve?: string[] | string;
    attributesToHighlight?: string[] | string;
    attributesToSnippet?: string[] | string;
    restrictSearchableAttributes?: string[] | string;
  };
};

/** Trailing spaces must not bypass the min length (e.g. "AI " → "AI"). */
const isSearchQueryTooShort = (query: string | undefined | null): boolean =>
  (query ?? '').trim().length < 2;

type IAiSearchClause =
  | { term: Record<string, string | number | boolean> }
  | {
      range: Record<
        string,
        { lt?: number; lte?: number; gt?: number; gte?: number }
      >;
    }
  | {
      bool: {
        must?: IAiSearchClause[];
        should?: IAiSearchClause[];
        must_not?: IAiSearchClause[];
        minimum_should_match?: number;
      };
    };

type IAiSearchMultiRequest = {
  requests: Array<{
    indexName: string;
    scope:
      | 'global_search'
      | 'member_directory'
      | 'organization_directory'
      | 'community';
    query: string;
    hitsPerPage: number;
    page: number;
    queryFilter: IAiSearchClause[];
    facets?: string[];
    attributesToRetrieve?: string[];
    attributesToHighlight?: string[];
    attributesToSnippet?: string[];
    restrictSearchableAttributes?: string[];
  }>;
};

type IAiSearchHit = Record<string, unknown> & {
  objectID: string;
};

type IAiSearchResult = {
  hits: IAiSearchHit[];
  nbHits: number;
  nbPages: number;
  page: number;
  processingTimeMS: number;
  hitsPerPage: number;
  exhaustiveNbHits: boolean;
  query: string;
  params: string;
  index: string;
};

type SearchBackend = 'algolia' | 'tradewing_iq';

type SearchAnalyticsResult = Partial<IAiSearchResult> & {
  hits?: Array<Record<string, unknown>>;
};

const EMPTY_RESULT = {
  hits: [],
  nbHits: 0,
  nbPages: 0,
  page: 0,
  processingTimeMS: 0,
  hitsPerPage: 0,
  exhaustiveNbHits: false,
  query: '',
  params: '',
  index: '',
};

const MAX_TRACKED_SEARCH_RESULTS = 10;
const SEARCH_ANALYTICS_SETTLE_MS = 600;
const pendingSearchAnalyticsTimers = new Map<
  string,
  ReturnType<typeof setTimeout>
>();

const LOGICAL_OPERATORS = new Set(['AND', 'OR', 'NOT']);

const isWrappedByParentheses = (value: string): boolean => {
  if (!value.startsWith('(') || !value.endsWith(')')) {
    return false;
  }

  let depth = 0;
  for (let i = 0; i < value.length; i += 1) {
    const char = value[i];
    if (char === '(') {
      depth += 1;
    } else if (char === ')') {
      depth -= 1;
      if (depth === 0 && i < value.length - 1) {
        return false;
      }
    }
  }

  return depth === 0;
};

const splitByTopLevelOperator = (
  input: string,
  operator: 'AND' | 'OR'
): string[] => {
  const parts: string[] = [];
  let depth = 0;
  let token = '';
  let i = 0;

  while (i < input.length) {
    const char = input[i];

    if (char === '(') {
      depth += 1;
      token += char;
      i += 1;
      continue;
    }

    if (char === ')') {
      depth -= 1;
      token += char;
      i += 1;
      continue;
    }

    if (
      depth === 0 &&
      input.slice(i, i + operator.length + 2) === ` ${operator} `
    ) {
      parts.push(token.trim());
      token = '';
      i += operator.length + 2;
      continue;
    }

    token += char;
    i += 1;
  }

  if (token.trim()) {
    parts.push(token.trim());
  }

  return parts;
};

const parsePrimitiveValue = (rawValue: string): string | number | boolean => {
  const normalized = rawValue.trim().replace(/^['"]|['"]$/g, '');

  if (normalized === 'true') {
    return true;
  }

  if (normalized === 'false') {
    return false;
  }

  if (normalized === '1') {
    return 1;
  }

  if (normalized === '0') {
    return 0;
  }

  const numeric = Number(normalized);
  if (!Number.isNaN(numeric) && normalized !== '') {
    return numeric;
  }

  return normalized;
};

const invertClause = (clause: IAiSearchClause): IAiSearchClause => {
  if ('term' in clause) {
    const [field, value] = Object.entries(clause.term)[0];
    if (typeof value === 'boolean') {
      return {
        term: {
          [field]: !value,
        },
      };
    }
  }

  if ('bool' in clause && clause.bool.must_not?.length) {
    return clause.bool.must_not[0];
  }

  return {
    bool: {
      must_not: [clause],
    },
  };
};

const parseComparisonClause = (input: string): IAiSearchClause | null => {
  const comparison = input.match(
    /^([A-Za-z0-9._-]+)\s*(<=|>=|!=|<|>|:|=)\s*(.+)$/
  );
  if (!comparison) {
    return null;
  }

  const [, field, operator, rawValue] = comparison;
  const value = parsePrimitiveValue(rawValue);

  if (operator === ':' || operator === '=') {
    return {
      term: {
        [field]: value,
      },
    };
  }

  if (operator === '!=') {
    return {
      bool: {
        must_not: [
          {
            term: {
              [field]: value,
            },
          },
        ],
      },
    };
  }

  if (typeof value !== 'number') {
    return null;
  }

  const rangeKey =
    operator === '<'
      ? 'lt'
      : operator === '<='
      ? 'lte'
      : operator === '>'
      ? 'gt'
      : 'gte';
  return {
    range: {
      [field]: {
        [rangeKey]: value,
      },
    },
  };
};

const parseFilterExpression = (expression: string): IAiSearchClause | null => {
  const trimmed = expression.trim();

  if (!trimmed) {
    return null;
  }

  if (isWrappedByParentheses(trimmed)) {
    return parseFilterExpression(trimmed.slice(1, -1));
  }

  const orParts = splitByTopLevelOperator(trimmed, 'OR');
  if (orParts.length > 1) {
    const clauses = orParts
      .map((part) => parseFilterExpression(part))
      .filter(Boolean) as IAiSearchClause[];

    if (!clauses.length) {
      return null;
    }

    return {
      bool: {
        should: clauses,
        minimum_should_match: 1,
      },
    };
  }

  const andParts = splitByTopLevelOperator(trimmed, 'AND');
  if (andParts.length > 1) {
    const clauses = andParts
      .map((part) => parseFilterExpression(part))
      .filter(Boolean) as IAiSearchClause[];

    if (!clauses.length) {
      return null;
    }

    return {
      bool: {
        must: clauses,
      },
    };
  }

  const tokens = trimmed.split(/\s+/);
  if (
    tokens[0] === 'NOT' &&
    tokens.length > 1 &&
    !LOGICAL_OPERATORS.has(tokens[1])
  ) {
    const clause = parseFilterExpression(trimmed.replace(/^NOT\s+/, ''));
    return clause ? invertClause(clause) : null;
  }

  return parseComparisonClause(trimmed);
};

const toQueryFilter = (filters?: string): IAiSearchClause[] => {
  if (!filters?.trim()) {
    return [];
  }

  const expression = parseFilterExpression(filters);

  if (!expression) {
    return [];
  }

  if (
    'bool' in expression &&
    expression.bool.must &&
    !expression.bool.should &&
    !expression.bool.must_not
  ) {
    return expression.bool.must;
  }

  return [expression];
};

const removeTenantIdClauses = (
  clause: IAiSearchClause
): IAiSearchClause | null => {
  if ('term' in clause) {
    return Object.prototype.hasOwnProperty.call(clause.term, 'tenantId')
      ? null
      : clause;
  }

  if ('range' in clause) {
    return clause;
  }

  const must = clause.bool.must
    ?.map((item) => removeTenantIdClauses(item))
    .filter(Boolean) as IAiSearchClause[] | undefined;
  const should = clause.bool.should
    ?.map((item) => removeTenantIdClauses(item))
    .filter(Boolean) as IAiSearchClause[] | undefined;
  const mustNot = clause.bool.must_not
    ?.map((item) => removeTenantIdClauses(item))
    .filter(Boolean) as IAiSearchClause[] | undefined;

  if (!must?.length && !should?.length && !mustNot?.length) {
    return null;
  }

  return {
    bool: {
      ...(must?.length ? { must } : {}),
      ...(should?.length
        ? {
            should,
            minimum_should_match: clause.bool.minimum_should_match ?? 1,
          }
        : {}),
      ...(mustNot?.length ? { must_not: mustNot } : {}),
    },
  };
};

const sanitizeQueryFilter = (
  queryFilter: IAiSearchClause[]
): IAiSearchClause[] =>
  queryFilter
    .map((clause) => removeTenantIdClauses(clause))
    .filter(Boolean) as IAiSearchClause[];

const normalizeStringArray = (
  value?: string[] | string
): string[] | undefined => {
  if (!value) {
    return undefined;
  }

  if (Array.isArray(value)) {
    return value;
  }

  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const getSearchTypeForIndex = (
  indexName: string,
  indexData?: ISearchContext['indexData']
): string => {
  if (!indexData) return 'global';
  if (indexName === indexData.memberIndexName) return 'members';
  if (indexName === indexData.postIndexName) return 'posts';
  if (indexName === indexData.eventIndexName) return 'events';
  if (indexName === indexData.orgIndexName) return 'organizations';
  if (indexName === indexData.fileIndexName) return 'files';
  return 'global';
};

const getSearchHitTitle = (hit: Record<string, unknown>): string | undefined => {
  const file = hit.file as { name?: unknown } | undefined;
  const blog = hit.blog as { title?: unknown } | undefined;
  const poll = hit.poll as { title?: unknown } | undefined;
  const question = hit.question as { title?: unknown } | undefined;

  const title =
    hit.name ??
    hit.title ??
    hit.displayName ??
    file?.name ??
    blog?.title ??
    poll?.title ??
    question?.title;

  return typeof title === 'string' && title.trim() ? title : undefined;
};

const getSearchHitId = (hit: Record<string, unknown>): string | undefined => {
  const id = hit.objectID ?? hit._id ?? hit.id;
  return typeof id === 'string' && id.trim() ? id : undefined;
};

const trackSearchResponse = (options: {
  backend: SearchBackend;
  queries: IInstantSearchQuery[];
  results: SearchAnalyticsResult[];
  indexData?: ISearchContext['indexData'];
}): void => {
  const query = options.queries
    .map((item) => item.params?.query?.trim() ?? '')
    .find(Boolean);

  if (!query || isSearchQueryTooShort(query)) {
    return;
  }

  const indexNames = options.queries.map((item) => item.indexName);
  const searchTypes = Array.from(
    new Set(indexNames.map((indexName) => getSearchTypeForIndex(indexName, options.indexData)))
  );
  const filters = options.queries.reduce<Record<string, string>>(
    (acc, item) => {
      if (item.params?.filters) {
        acc[item.indexName] = item.params.filters;
      }
      return acc;
    },
    {}
  );
  const trackedResults = options.results
    .flatMap((result, resultIndex) => {
      const indexName = result.index ?? indexNames[resultIndex] ?? '';
      const resultType = getSearchTypeForIndex(indexName, options.indexData);

      return (result.hits ?? []).map((hit, position) => ({
        id: getSearchHitId(hit),
        type: resultType,
        title: getSearchHitTitle(hit),
        indexName,
        position,
      }));
    })
    .filter((item) => item.id || item.title)
    .slice(0, MAX_TRACKED_SEARCH_RESULTS);
  const resultCount = options.results.reduce(
    (total, result) =>
      total + (typeof result.nbHits === 'number' ? result.nbHits : 0),
    0
  );

  const analyticsPayload = {
    entity_id: query,
    entity_type: 'search',
    search_backend: options.backend,
    search_query: query,
    search_query_length: query.length,
    search_type: searchTypes.length === 1 ? searchTypes[0] : 'global',
    search_result_count: resultCount,
    search_has_results: resultCount > 0,
    search_filters: filters,
    search_has_filters: Object.keys(filters).length > 0,
    search_index_names: indexNames,
    search_results: trackedResults,
  };
  const trackingScope = `${options.backend}:${searchTypes.join(',')}`;
  const pendingTimer = pendingSearchAnalyticsTimers.get(trackingScope);
  if (pendingTimer) clearTimeout(pendingTimer);
  pendingSearchAnalyticsTimers.set(
    trackingScope,
    setTimeout(() => {
      pendingSearchAnalyticsTimers.delete(trackingScope);
      search_query_submitted(analyticsPayload);
    }, SEARCH_ANALYTICS_SETTLE_MS)
  );
};

const getDefaultHighlightAndSnippetAttributes = (options: {
  indexName: string;
  indexData: NonNullable<ISearchContext['indexData']>;
}): {
  attributesToHighlight: string[];
  attributesToSnippet: string[];
} => {
  const { indexName, indexData } = options;

  if (indexName === indexData.postIndexName) {
    return {
      attributesToHighlight: ['title', 'bodyPlainText'],
      attributesToSnippet: ['*'],
    };
  }

  if (indexName === indexData.memberIndexName) {
    return {
      attributesToHighlight: ['displayName', 'adminGlobalSearchable'],
      attributesToSnippet: ['*'],
    };
  }

  if (indexName === indexData.orgIndexName) {
    return {
      attributesToHighlight: ['name', 'aboutUs'],
      attributesToSnippet: ['*'],
    };
  }

  if (indexName === indexData.eventIndexName) {
    return {
      attributesToHighlight: ['name', 'descriptionPlainText'],
      attributesToSnippet: ['*'],
    };
  }

  if (indexName === indexData.fileIndexName) {
    return {
      attributesToHighlight: ['file.name', 'bodyPlainText'],
      attributesToSnippet: ['*'],
    };
  }

  return {
    attributesToHighlight: ['*'],
    attributesToSnippet: ['*'],
  };
};

const SearchContext = React.createContext<ISearchContext>({
  client: null,
  searchMode: 'algolia',
  aiSearchHttp: null,
});

export const useGlobalSearchContext = (): ISearchContext =>
  React.useContext(SearchContext);

export const getSearchKeysAST = gql`
  query getSearchKeys {
    getSearchKeys {
      applicationId
      searchApiKey
      indexData {
        postIndexName
        orgIndexName
        eventIndexName
        fileIndexName
        memberIndexName
        memberIndexSearchableField
        memberIndexViewableField
      }
      indexFilterData {
        memberIndexFilter
        orgIndexFilter
      }
    }
  }
`;

export const SearchProvider: React.FC<{ isAuthenticated: boolean }> = (
  props
) => {
  const current = useCurrent();
  const { error, data } = useQueryWithPastDataDeprecated<getSearchKeys>(
    getSearchKeysAST,
    {
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      skip: !props.isAuthenticated,
    }
  );
  const router = useRouter();
  const [searchClient, setSearchClient] =
    React.useState<ISearchClientProxy | null>(null);
  const lastClientSignatureRef = React.useRef<string | null>(null);
  const tenantSearchMode = (current?.tenant as { searchMode?: unknown } | null)
    ?.searchMode;
  const shouldUseAlgolia =
    tenantSearchMode === TenantSearchMode.AI ? false : true;

  const aiSearchHttp = React.useMemo((): AiSearchHttpConfig | null => {
    if (shouldUseAlgolia) return null;
    if (!current.user?._id || !current.tenant?._id) return null;
    const baseUrl = (
      process.env.NEXT_PUBLIC_AI_SEARCH_BASE_URL ?? '/api/v1/ai-search'
    ).replace(/\/$/, '');
    return {
      baseUrl,
      buildHeaders: (): HeadersInit => ({
        'Content-Type': 'application/json',
        'x-user-id': current.user?._id ?? '',
        'x-tenant-id': current.tenant?._id ?? '',
        Authorization: `Bearer ${cookie.get('accounts%3AaccessToken')}`,
        ...(cookie.get('accounts%3ArefreshToken')
          ? {
              'x-refresh-token': cookie.get(
                'accounts%3ArefreshToken'
              ) as string,
            }
          : {}),
      }),
    };
  }, [current.user?._id, current.tenant?._id, shouldUseAlgolia]);

  React.useEffect(() => {
    if (data) {
      const clientSignature = [
        shouldUseAlgolia ? 'algolia' : 'ai',
        data.getSearchKeys.applicationId,
        data.getSearchKeys.searchApiKey,
        current.user?._id ?? '',
        current.tenant?._id ?? '',
      ].join('|');

      if (lastClientSignatureRef.current === clientSignature) {
        return;
      }

      if (shouldUseAlgolia) {
        // Use legacy Algolia client behavior when tenant mode is Algolia.
        const algoliaClient = algoliasearch(
          data.getSearchKeys.applicationId,
          data.getSearchKeys.searchApiKey
        );
        const queryResponseCache = new Map<
          string,
          { results: Array<Record<string, unknown>> }
        >();

        const searchClientProxy: ISearchClientProxy = {
          async search(queriesInput, requestOptions) {
            const queries = queriesInput as IInstantSearchQuery[];
            const cacheKey = JSON.stringify({
              queries: queriesInput,
              requestOptions,
            });

              if (
                queries.every(({ params }) =>
                  isSearchQueryTooShort(params?.query)
                )
              ) {
              return Promise.resolve({
                results: queries.map(() => ({
                  hits: [],
                  nbHits: 0,
                  nbPages: 0,
                  page: 0,
                  processingTimeMS: 0,
                  hitsPerPage: 0,
                  exhaustiveNbHits: false,
                  query: '',
                  params: '',
                  index: '',
                })),
              });
            }

            if (queryResponseCache.has(cacheKey)) {
              return queryResponseCache.get(cacheKey)!;
            }

            const response = await (
              algoliaClient.search as unknown as ISearchClientProxy['search']
            )(queriesInput, requestOptions);
            trackSearchResponse({
              backend: 'algolia',
              queries,
              results: response.results,
              indexData: data.getSearchKeys.indexData,
            });
            queryResponseCache.set(cacheKey, response);

            return response;
          },
          async clearCache() {
            queryResponseCache.clear();
            await algoliaClient.clearCache();
          },
        };

        setSearchClient(searchClientProxy);
        lastClientSignatureRef.current = clientSignature;
        return;
      }

      const aiSearchBaseUrl =
        process.env.NEXT_PUBLIC_AI_SEARCH_BASE_URL ?? '/api/v1/ai-search';
      const normalizedBaseUrl = aiSearchBaseUrl.replace(/\/$/, '');
      const queryResponseCache = new Map<
        string,
        { results: Array<Record<string, unknown>> }
      >();

      // Keep the old Algolia initialization as reference during migration.
      // const client = algoliasearch(
      //   data.getSearchKeys.applicationId,
      //   data.getSearchKeys.searchApiKey
      // );

      // https://www.algolia.com/doc/guides/building-search-ui/going-further/conditional-requests/js/
      const searchClientProxy: ISearchClientProxy = {
        async search(queriesInput, _requestOptions) {
          const queries = queriesInput as IInstantSearchQuery[];
          const cacheKey = JSON.stringify({ queries: queriesInput });

          if (
            queries.every(({ params }) => isSearchQueryTooShort(params?.query))
          ) {
            return Promise.resolve({
              results: queries.map(() => ({
                hits: [],
                nbHits: 0,
                nbPages: 0,
                page: 0,
                processingTimeMS: 0,
                hitsPerPage: 0,
                exhaustiveNbHits: false,
                query: '',
                params: '',
                index: '',
              })),
            });
          }

          if (queryResponseCache.has(cacheKey)) {
            return queryResponseCache.get(cacheKey)!;
          }

          const payload: IAiSearchMultiRequest = {
            requests: queries.map((query) => ({
              ...getDefaultHighlightAndSnippetAttributes({
                indexName: query.indexName,
                indexData: data.getSearchKeys.indexData,
              }),
              indexName: query.indexName,
              scope: 'global_search',
              query: (query.params?.query ?? '').trim(),
              hitsPerPage: query.params?.hitsPerPage ?? 20,
              page: query.params?.page ?? 0,
              facets: normalizeStringArray(query.params?.facets),
              queryFilter: sanitizeQueryFilter(
                toQueryFilter(query.params?.filters)
              ),
              attributesToRetrieve: normalizeStringArray(
                query.params?.attributesToRetrieve
              ),
              attributesToHighlight:
                normalizeStringArray(query.params?.attributesToHighlight) ||
                getDefaultHighlightAndSnippetAttributes({
                  indexName: query.indexName,
                  indexData: data.getSearchKeys.indexData,
                }).attributesToHighlight,
              attributesToSnippet: normalizeStringArray(
                query.params?.attributesToSnippet
              ) || ['*'],
              restrictSearchableAttributes: normalizeStringArray(
                query.params?.restrictSearchableAttributes
              ),
            })),
          };

          const requestId = crypto.randomUUID();
          console.info('AI search request', {
            requestId,
            requests: payload.requests.map(({ indexName, scope, query }) => ({
              indexName,
              scope,
              query,
            })),
          });

          const response = await fetch(
            `${normalizedBaseUrl}/1/indexes/*/queries`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'x-request-id': requestId,
                'x-user-id': current.user?._id ?? '',
                'x-tenant-id': current.tenant?._id ?? '',
                Authorization: `Bearer ${cookie.get('accounts%3AaccessToken')}`,
                ...(cookie.get('accounts%3ArefreshToken')
                  ? {
                      'x-refresh-token': cookie.get(
                        'accounts%3ArefreshToken'
                      ) as string,
                    }
                  : {}),
              },
              body: JSON.stringify(payload),
            }
          );

          if (!response.ok) {
            throw new Error(
              `AI Search request failed with status ${response.status}`
            );
          }
          const result = (await response.json()) as {
            results?: Array<Partial<IAiSearchResult>>;
          };

          console.info('AI search response', {
            requestId: response.headers.get('x-request-id') ?? requestId,
            status: response.status,
            totals: (result.results ?? []).map((item) => item.nbHits ?? 0),
          });

          const rawResults = (result.results ?? []).map(
            (searchResult, index) => ({
              ...EMPTY_RESULT,
              ...searchResult,
              index: searchResult.index ?? queries[index]?.indexName ?? '',
            })
          );

          const resultsByIndex = new Map<string, Array<IAiSearchResult>>();

          rawResults.forEach((searchResult) => {
            const indexResults = resultsByIndex.get(searchResult.index) ?? [];
            indexResults.push(searchResult);
            resultsByIndex.set(searchResult.index, indexResults);
          });

          const mappedResponse = {
            results: queries.map((query) => {
              const indexResults = resultsByIndex.get(query.indexName);

              if (indexResults?.length) {
                return indexResults.shift() as IAiSearchResult;
              }

              return {
                ...EMPTY_RESULT,
                index: query.indexName,
              };
            }),
          };

          trackSearchResponse({
            backend: 'tradewing_iq',
            queries,
            results: mappedResponse.results,
            indexData: data.getSearchKeys.indexData,
          });
          queryResponseCache.set(cacheKey, mappedResponse);

          return mappedResponse;
        },
        async clearCache() {
          queryResponseCache.clear();
          return Promise.resolve();
        },
      };
      setSearchClient(searchClientProxy);
      lastClientSignatureRef.current = clientSignature;
    }
  }, [data, shouldUseAlgolia, current.user?._id, current.tenant?._id]);

  if (error) {
    logError(error);
  }

  return (
    <SearchContext.Provider
      value={{
        client: searchClient,
        searchMode: shouldUseAlgolia ? 'algolia' : 'opensearch',
        indexData: data?.getSearchKeys.indexData,
        indexFilterData: data?.getSearchKeys.indexFilterData,
        aiSearchHttp,
      }}
    >
      {props.children}
    </SearchContext.Provider>
  );
};
